import React from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Select,
} from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import { exportExpensesToPDF } from "../../utils/exportExpenses";

import { formatJalaliDateTime } from "../utils/dateFormat";
import { JalaliDatePickerField } from "../Components/JalaliDateField";
export default function ExpensesTable({ selectedBranch = "" }) {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  React.useEffect(() => {
    setPage(1);
  }, [startDate, endDate, selectedCategory]);

  const queryString = React.useMemo(() => {
    const params = [];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    if (selectedCategory) params.push(`category=${selectedCategory}`);
    params.push(`page=${page}`);
    if (rowsPerPage !== -1) {
      params.push(`page_size=${rowsPerPage}`);
    } else {
      params.push(`page_size=10000`);
    }
    return params.join("&");
  }, [startDate, endDate, selectedBranch, selectedCategory, page, rowsPerPage]);

  const {
    data: expensesData,
    isLoading,
    isError,
  } = useFetchObjects(
    ["expenses", queryString, selectedCategory],
    `shop/expenses/?${queryString}`,
    token
  );

  // Fetch expense categories for filter dropdown
  const { data: categoriesData } = useFetchObjects(
    ["expense-categories"],
    "shop/expense-categories",
    token
  );

  const categories = categoriesData?.results || [];

  const expenses = expensesData?.results || [];
  const filtersActive = Boolean(startDate || endDate || selectedCategory);

  const filteredExpenses = React.useMemo(() => {
    let filtered = expenses;
    if (selectedCategory) {
      filtered = filtered.filter(
        (expense) =>
          String(expense.category) === String(selectedCategory) ||
          String(expense.category_details?.id) === String(selectedCategory)
      );
    }
    if (startDate || endDate) {
      filtered = filtered.filter((expense) => {
        if (!expense.payment_date) return false;
        const date = dayjs(expense.payment_date);
        if (startDate && date.isBefore(startDate, "day")) return false;
        if (endDate && date.isAfter(endDate, "day")) return false;
        return true;
      });
    }
    return filtered;
  }, [expenses, selectedCategory, startDate, endDate]);

  const totalCount = filtersActive
    ? filteredExpenses.length
    : expensesData?.count || 0;

  // Calculate summary totals
  const summaryTotals = React.useMemo(() => {
    return filteredExpenses.reduce((acc, expense) => {
      const amount = parseFloat(expense.amount) || 0;
      
      return {
        totalAmount: acc.totalAmount + amount
      };
    }, { totalAmount: 0 });
  }, [filteredExpenses]);

  const displayExpenses = React.useMemo(() => {
    if (rowsPerPage === -1) return filteredExpenses;
    const startIndex = (page - 1) * rowsPerPage;
    return filteredExpenses.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredExpenses, page, rowsPerPage]);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `expenses_${today}`;
    if (type === "pdf") exportExpensesToPDF(filteredExpenses, `${base}.pdf`);
    handleClose();
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          {t("ReportIndex.error")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Filters */}
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <JalaliDatePickerField
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <JalaliDatePickerField
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <FormControl fullWidth>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_categories")}</MenuItem>
                    {(categories || []).map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedCategory("");
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{ "aria-labelledby": "export-button" }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, ml: 2 }}>
        <Typography>{t("reports.rowsPerPage")}</Typography>
        <FormControl size="small">
          <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={-1}>{t("reports.all")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider />

      {/* Expenses Table */}
      <TableContainer component={Paper}>
        <Table aria-label="expenses table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography variant="h4">
                  {t("ReportIndex.expenses_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("ReportIndex.expenses_table_id")}</TableCell>
              <TableCell>{t("ReportIndex.expenses_table_category", "Expense Category")}</TableCell>
              <TableCell>{t("ReportIndex.expenses_table_expenseBy")}</TableCell>
              <TableCell>{t("ReportIndex.expenses_table_amount")}</TableCell>
              <TableCell>
                {t("ReportIndex.expenses_table_paymentDate")}
              </TableCell>
              <TableCell>{t("ReportIndex.expenses_table_notes")}</TableCell>
              <TableCell>{t("ReportIndex.expenses_table_createdAt")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayExpenses.map((expense) => (
              <TableRow key={expense.id}>
                <TableCell>{expense.id}</TableCell>
                <TableCell>{expense.category_details?.name || "-"}</TableCell>
                <TableCell>{expense.expense_by || "-"}</TableCell>
                <TableCell>
                  {expense.amount || "0"}{" "}
                  {expense.currency_details?.title || "-"}
                </TableCell>
                <TableCell>
                  {expense.payment_date
                    ? dayjs(expense.payment_date).format("YYYY-MM-DD")
                    : "-"}
                </TableCell>
                <TableCell>{expense.notes || "-"}</TableCell>
                <TableCell>
                  {expense.created_at
                    ? formatJalaliDateTime(expense.created_at)
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalAmount.toFixed(2)}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {rowsPerPage !== -1 && (
        <Grid container justifyContent="center" alignItems="center" padding="10px">
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onChange={(event, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
          />
        </Grid>
      )}
    </>
  );
}
